import { useMemo } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";

import useUrlParams from "Utilities/Hooks/useUrlParams";

/**
 * Useful to style links as "active"
 * (akin to RR's built-in functionality: https://v5.reactrouter.com/web/api/NavLink/activeclassname-string).
 * Returns true if "to" matches the current route, and false otherwise.
 * @param   {string}  to - MUST have a leading slash if internal
 * @returns {boolean}
 */
export default function useIsSelected(to) {
  const toMatch = useRouteMatch(to);
  const location = useLocation();
  const { fund, batch } = useUrlParams();
  // The Giving Summary link's `to` is `/giving`, but the Summary page also
  // lives at `/giving/summary` and its sub-tabs (`/cash-flow`, `/reach-outs`).
  // Non-exact match keeps Summary selected across those sub-routes.
  const givingSummaryMatch = useRouteMatch("/giving/summary");

  return useMemo(() => {
    // Default strategy
    if (toMatch && !EXPLICITLY_DESELECTED_PARENTISH_DESTINATIONS.has(to)) {
      return true;
    }
    if (toMatch?.path === "/giving" && !fund && !batch && toMatch.isExact) {
      return true;
    }
    // Summary link stays selected on the /giving/summary route + its sub-tabs.
    if (to === "/giving" && givingSummaryMatch) {
      return true;
    }

    // Fund detail routes: /giving/funds/:fundID
    if (getIsFundPathLocation(to)) {
      return (
        getIsFundPathLocation(location.pathname) &&
        getFundIDFromPath(to) === getFundIDFromPath(location.pathname)
      );
    }

    /**
     * This is a bit of a hacky way to preserve our desired "selected" indication
     * while viewing a profile, which can happen on _any route_.
     * Because we render the profile overlay by simply appending a
     * `/user/:userID` to the pathname and adding a `profileTab` search param,
     * below we are manually stripping these out of the URL
     * before proceeding with our other matching logic.
     *
     * Ideally, nothing beyond a simple route match would be necessary in
     * a future iteration of our routing setup.
     */

    const pathnameWithoutProfileSegment = location.pathname.replace(
      USER_PATH_SEGMENT,
      ""
    );
    const searchWithoutProfileParams = getNonProfileSearchParams(
      location.search
    );

    const locationWithoutProfile = `${pathnameWithoutProfileSegment}${
      searchWithoutProfileParams.length > 0
        ? `?${searchWithoutProfileParams}`
        : ""
    }`;

    // Fallback for exact top-level matches being thrown off by the user profile being open
    // e.g./householddatabase/user/1?profileTab=newsfeed
    return locationWithoutProfile === to;
  }, [
    batch,
    fund,
    givingSummaryMatch,
    location.pathname,
    location.search,
    to,
    toMatch,
  ]);
}

const EXPLICITLY_DESELECTED_PARENTISH_DESTINATIONS = Object.freeze(
  /**
   * While viewing Family Reg (`/householddatabase/familyregistration`)
   * we do _not_ want to indicate HHDB (`/householddatabase`) as selected.
   * While viewing a sub-route like `/giving/funds/1`,
   * we do _not_ want to indicate the Giving Summary (`/giving`) as selected.
   * NB: the leading slashes are important!
   */
  new Set(["/householddatabase", "/giving"])
);

const USER_PATH_SEGMENT = /\/user\/\d+/;

const PROFILE_SEARCH_PARAMS = Object.freeze(
  new Set(["profileTab", "householdID"])
);

/**
 * Returns a valid search params string with profile params removed
 * @param   {string} searchParams
 * @returns {string}
 */
function getNonProfileSearchParams(searchParams) {
  const params = new URLSearchParams(searchParams);
  PROFILE_SEARCH_PARAMS.forEach((param) => {
    if (params.has(param)) params.delete(param);
  });
  return params.toString();
}

const FUND_PATH_PATTERN = /^\/giving\/funds\/(\d+)/;

/**
 * @param   {string}  location
 * @returns {boolean}
 */
function getIsFundPathLocation(location) {
  return FUND_PATH_PATTERN.test(location);
}

/**
 * @param   {string}           location
 * @returns {number|undefined}
 */
function getFundIDFromPath(location) {
  const match = location.match(FUND_PATH_PATTERN);
  if (!match) return undefined;
  return parseInt(match[1]);
}
